import { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

// ── Types ──────────────────────────────────────────────────────────────────

type ClubOption = {
  club_id: string;
  clubs: { id: string; slug: string; name: string; logo: string | null } | null;
};

type FollowerRow = {
  id: string;
  user_id: string;
  created_at: string;
  profiles: {
    display_name: string | null;
    profile_image: string | null;
    experience_level: string | null;
    pace_range: string | null;
  } | null;
};

type RunRow = {
  id: string;
  title: string | null;
  day_of_week: string | null;
  start_time: string | null;
  is_recurring: boolean | null;
  run_date: string | null; // one-off runs have an explicit date
  nextDate: string; // computed
};

const DAY_MAP: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

function nextOccurrence(run: Omit<RunRow, 'nextDate'>, todayStr: string): string {
  if (!run.is_recurring || !run.day_of_week) return run.run_date || todayStr;
  const today = new Date(todayStr + 'T00:00:00');
  const target = DAY_MAP[run.day_of_week.toLowerCase()] ?? today.getDay();
  const daysUntil = (target - today.getDay() + 7) % 7;
  const next = new Date(today);
  next.setDate(today.getDate() + daysUntil);
  return next.toISOString().split('T')[0];
}

type RsvpRow = { run_id: string; run_date: string; status: 'going' | 'interested' };

type Stats = {
  totalFollowers: number;
  newFollowersThisWeek: number;
  pageViewsThisWeek: number;
};

// ── Small shared components ────────────────────────────────────────────────

function MiniAvatar({ name, image }: { name: string; image?: string | null }) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-7 w-7 rounded-full object-cover border border-black/10 flex-shrink-0"
      />
    );
  }
  return (
    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-black/8 text-[10px] font-bold text-black/50">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  positive,
}: {
  label: string;
  value: number;
  loading: boolean;
  positive?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-3 w-16 rounded bg-black/8" />
          <div className="h-7 w-10 rounded bg-black/8" />
        </div>
      ) : (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-black/40">
            {label}
          </p>
          <p className={`mt-2 text-3xl font-black ${positive && value > 0 ? 'text-emerald-600' : 'text-black'}`}>
            {value.toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-black/40">{title}</h2>
      {children}
    </div>
  );
}

function EmptyState({ icon, message, hint }: { icon: string; message: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-black/10 bg-white px-6 py-8 text-center">
      <p className="text-3xl">{icon}</p>
      <p className="mt-3 text-sm font-semibold text-black">{message}</p>
      {hint && <p className="mt-1 text-xs text-black/45">{hint}</p>}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function LeaderDashboard() {
  const { leader, loading: authLoading } = useAuth();

  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);

  const [stats, setStats] = useState<Stats | null>(null);
  const [followers, setFollowers] = useState<FollowerRow[]>([]);
  const [instances, setInstances] = useState<RunRow[]>([]);
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // ── Load leader's clubs ──────────────────────────────────────────────────

  useEffect(() => {
    if (!leader) return;
    setClubsLoading(true);
    supabase
      .from('club_leaders')
      .select('club_id, clubs(id, slug, name, logo)')
      .eq('leader_id', leader.id)
      .then(({ data }) => {
        const rows = (data as unknown as ClubOption[]) || [];
        setClubs(rows);
        if (rows.length === 1) setSelectedClubId(rows[0].club_id);
        setClubsLoading(false);
      });
  }, [leader]);

  // ── Fetch dashboard data for selected club ───────────────────────────────

  const fetchDashboard = useCallback(async (clubId: string) => {
    setLoading(true);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const today = new Date().toISOString().split('T')[0];

    const [followsRes, pageViewsRes, runsRes] = await Promise.all([
      supabase
        .from('club_follows')
        .select('id, user_id, created_at, profiles(display_name, profile_image, experience_level, pace_range)')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false }),
      supabase
        .from('club_page_views')
        .select('id', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .gte('viewed_at', weekAgo),
      supabase
        .from('runs')
        .select('id, title, day_of_week, start_time, is_recurring, run_date')
        .eq('club_id', clubId)
        .eq('is_active', true),
    ]);

    // Compute next occurrence for each run and sort ascending
    const rawRuns = (runsRes.data || []) as Omit<RunRow, 'nextDate'>[];
    const upcomingRuns: RunRow[] = rawRuns
      .map((r) => ({ ...r, nextDate: nextOccurrence(r, today) }))
      .filter((r) => r.nextDate >= today)
      .sort((a, b) => a.nextDate.localeCompare(b.nextDate))
      .slice(0, 8);

    // Fetch RSVPs for these specific runs + their next dates
    const runIds = upcomingRuns.map((r) => r.id);
    const rsvpsRes = runIds.length
      ? await supabase
          .from('run_rsvps')
          .select('run_id, run_date, status')
          .eq('club_id', clubId)
          .in('run_id', runIds)
          .in('status', ['going', 'interested'])
      : { data: [] };

    const allFollows = (followsRes.data as unknown as FollowerRow[]) || [];
    const newThisWeek = allFollows.filter((f) => f.created_at >= weekAgo).length;

    setStats({
      totalFollowers: allFollows.length,
      newFollowersThisWeek: newThisWeek,
      pageViewsThisWeek: pageViewsRes.count ?? 0,
    });
    setFollowers(allFollows);
    setInstances(upcomingRuns);
    setRsvps((rsvpsRes.data as unknown as RsvpRow[]) || []);
    setLastRefreshed(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedClubId) fetchDashboard(selectedClubId);
  }, [selectedClubId, fetchDashboard]);

  // Auto-refresh every hour
  useEffect(() => {
    if (!selectedClubId) return;
    const interval = setInterval(() => fetchDashboard(selectedClubId), 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedClubId, fetchDashboard]);

  // ── Auth guard ───────────────────────────────────────────────────────────

  if (authLoading || clubsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!leader) return <Navigate to="/auth" replace />;

  // ── Helpers ──────────────────────────────────────────────────────────────

  const selectedClub = clubs.find((c) => c.club_id === selectedClubId);

  function rsvpCounts(runId: string, nextDate: string) {
    return {
      going: rsvps.filter((r) => r.run_id === runId && r.run_date === nextDate && r.status === 'going').length,
      interested: rsvps.filter((r) => r.run_id === runId && r.run_date === nextDate && r.status === 'interested').length,
    };
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-24">
      <div className="mx-auto max-w-3xl px-4 pt-8 sm:px-6">

        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/40">Leader</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-black sm:text-4xl">Dashboard</h1>
          </div>
          {lastRefreshed && (
            <button
              onClick={() => selectedClubId && fetchDashboard(selectedClubId)}
              disabled={loading}
              className="mt-1 shrink-0 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs text-black/40 transition hover:text-black disabled:opacity-40"
            >
              {loading ? 'Refreshing…' : `↻ ${lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </button>
          )}
        </div>

        {/* No clubs */}
        {clubs.length === 0 && (
          <EmptyState
            icon="🏃"
            message="No clubs yet"
            hint="Create your first club to start tracking followers and RSVPs."
          />
        )}

        {/* Multiple clubs — pick one */}
        {clubs.length > 1 && !selectedClubId && (
          <div className="space-y-3">
            <p className="text-sm text-black/50">Select a club to view its dashboard:</p>
            {clubs.map((c) =>
              c.clubs ? (
                <button
                  key={c.club_id}
                  onClick={() => setSelectedClubId(c.club_id)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-black/10 bg-white p-4 text-left transition hover:bg-black/2"
                >
                  {c.clubs.logo ? (
                    <img src={c.clubs.logo} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/8 text-sm font-bold text-black/40">
                      {c.clubs.name.charAt(0)}
                    </div>
                  )}
                  <span className="font-semibold text-black">{c.clubs.name}</span>
                </button>
              ) : null
            )}
          </div>
        )}

        {/* Dashboard for selected club */}
        {selectedClub?.clubs && (
          <div className="space-y-8">
            {/* Club title */}
            <div className="flex items-center gap-3">
              {clubs.length > 1 && (
                <button
                  onClick={() => setSelectedClubId(null)}
                  className="text-xs text-black/40 hover:text-black transition"
                >
                  ← All clubs
                </button>
              )}
              {selectedClub.clubs.logo && (
                <img
                  src={selectedClub.clubs.logo}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover border border-black/10"
                />
              )}
              <div>
                <p className="font-bold text-black">{selectedClub.clubs.name}</p>
                <Link
                  to={`/clubs/${selectedClub.clubs.slug}`}
                  className="text-xs text-black/40 transition hover:text-black"
                >
                  View club page ↗
                </Link>
              </div>
            </div>

            {/* Stats */}
            <Section title="Overview">
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Followers" value={stats?.totalFollowers ?? 0} loading={loading} />
                <StatCard label="New this week" value={stats?.newFollowersThisWeek ?? 0} loading={loading} positive />
                <StatCard label="Views this week" value={stats?.pageViewsThisWeek ?? 0} loading={loading} />
              </div>
            </Section>

            {/* Upcoming runs */}
            <Section title="Upcoming Runs">
              {loading ? (
                <div className="animate-pulse space-y-px rounded-2xl overflow-hidden border border-black/10">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 bg-black/5" />
                  ))}
                </div>
              ) : instances.length === 0 ? (
                <EmptyState
                  icon="📅"
                  message="No upcoming runs scheduled"
                  hint="Add runs from your club management page to see RSVP counts here."
                />
              ) : (
                <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
                  {instances.map((inst) => {
                    const { going, interested } = rsvpCounts(inst.id, inst.nextDate);
                    const runTitle = inst.title || inst.day_of_week || 'Run';
                    const startTime = inst.start_time || '';
                    const dateLabel = new Date(inst.nextDate + 'T12:00:00').toLocaleDateString(
                      'en-US',
                      { weekday: 'short', month: 'short', day: 'numeric' }
                    );
                    return (
                      <div
                        key={inst.id}
                        className="flex items-center justify-between gap-4 border-b border-black/5 px-4 py-3.5 last:border-0"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-black">{runTitle}</p>
                          <p className="text-xs text-black/40">
                            {dateLabel}
                            {startTime ? ` · ${startTime}` : ''}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-3 text-xs font-semibold">
                          <span className="flex items-center gap-1.5 text-emerald-700">
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            {going}
                          </span>
                          <span className="flex items-center gap-1.5 text-yellow-700">
                            <span className="h-2 w-2 rounded-full bg-yellow-400" />
                            {interested}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!loading && instances.length > 0 && (
                <p className="text-right text-[10px] text-black/30">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Going
                    <span className="ml-2 h-1.5 w-1.5 rounded-full bg-yellow-400" /> Interested
                  </span>
                </p>
              )}
            </Section>

            {/* Followers list */}
            <Section title={`Followers${stats && stats.totalFollowers > 0 ? ` · ${stats.totalFollowers}` : ''}`}>
              {loading ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded-xl bg-black/5" />
                  ))}
                </div>
              ) : followers.length === 0 ? (
                <EmptyState
                  icon="👋"
                  message="No followers yet — you're just getting started!"
                  hint="Share your club page on Instagram or hand out the link at your next run to get your first followers."
                />
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {followers.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center gap-2 rounded-xl border border-black/8 bg-white px-3 py-2.5"
                    >
                      <MiniAvatar
                        name={f.profiles?.display_name || 'Runner'}
                        image={f.profiles?.profile_image}
                      />
                      <p className="truncate text-xs font-medium text-black">
                        {f.profiles?.display_name || 'Runner'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Audience breakdown */}
            {!loading && followers.length > 0 && (() => {
              const withData = followers.filter((f) => f.profiles?.experience_level || f.profiles?.pace_range);
              if (withData.length === 0) return null;

              const expCounts: Record<string, number> = {};
              const paceCounts: Record<string, number> = {};
              for (const f of followers) {
                if (f.profiles?.experience_level) {
                  expCounts[f.profiles.experience_level] = (expCounts[f.profiles.experience_level] || 0) + 1;
                }
                if (f.profiles?.pace_range) {
                  paceCounts[f.profiles.pace_range] = (paceCounts[f.profiles.pace_range] || 0) + 1;
                }
              }

              const expEntries = Object.entries(expCounts).sort((a, b) => b[1] - a[1]);
              const paceEntries = Object.entries(paceCounts).sort((a, b) => b[1] - a[1]);
              const expMax = Math.max(...expEntries.map(([, n]) => n), 1);
              const paceMax = Math.max(...paceEntries.map(([, n]) => n), 1);

              return (
                <Section title="Audience Breakdown">
                  <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm divide-y divide-black/5">
                    {expEntries.length > 0 && (
                      <div className="px-5 py-4">
                        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-black/40">Experience</p>
                        <div className="space-y-2">
                          {expEntries.map(([level, count]) => (
                            <div key={level} className="flex items-center gap-3">
                              <span className="w-24 shrink-0 text-xs font-medium text-black">{level}</span>
                              <div className="flex-1 overflow-hidden rounded-full bg-black/6 h-2">
                                <div
                                  className="h-full rounded-full bg-black transition-all"
                                  style={{ width: `${(count / expMax) * 100}%` }}
                                />
                              </div>
                              <span className="w-5 shrink-0 text-right text-xs text-black/40">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {paceEntries.length > 0 && (
                      <div className="px-5 py-4">
                        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.14em] text-black/40">Pace (min/mile)</p>
                        <div className="space-y-2">
                          {paceEntries.map(([pace, count]) => (
                            <div key={pace} className="flex items-center gap-3">
                              <span className="w-24 shrink-0 text-xs font-medium text-black">{pace}</span>
                              <div className="flex-1 overflow-hidden rounded-full bg-black/6 h-2">
                                <div
                                  className="h-full rounded-full bg-black transition-all"
                                  style={{ width: `${(count / paceMax) * 100}%` }}
                                />
                              </div>
                              <span className="w-5 shrink-0 text-right text-xs text-black/40">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              );
            })()}
          </div>
        )}
      </div>
    </main>
  );
}
